import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Copy, ShieldCheck, KeyRound, Server, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

function verdictColor(v) {
  const s = (v || "").toLowerCase();
  if (s === "pass") return "text-emerald-brand bg-emerald-brand/12";
  if (s === "fail" || s === "temperror") return "text-crimson bg-crimson/12";
  if (s === "none" || s === "neutral" || s === "softfail" || s === "policy") return "text-yellow-700 bg-sun/20";
  return "text-muted-foreground bg-muted";
}

function InlineCopy({ value, testid }) {
  const [c, setC] = useState(false);
  const doCopy = async () => {
    try { await navigator.clipboard.writeText(String(value ?? "")); setC(true); toast.success("Copied"); setTimeout(() => setC(false), 1200); }
    catch { toast.error("Copy failed"); }
  };
  if (!value) return null;
  return (
    <button type="button" onClick={doCopy} className="text-muted-foreground hover:text-emerald-brand transition-colors" title="Copy" data-testid={testid}>
      {c ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

/**
 * SenderDetailsPanel — Gmail-style expandable header details for one email.
 * Fetches /api/emails/{id}/sender-details lazily on first expand.
 */
export default function SenderDetailsPanel({ emailId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setData(null); setOpen(false); }, [emailId]);

  const load = async () => {
    if (data || loading) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/emails/${emailId}/sender-details`);
      setData(d);
    } catch (e) {
      toast.error("Failed to load sender details");
    }
    setLoading(false);
  };

  const toggle = () => { setOpen((v) => !v); if (!data) load(); };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Intl.DateTimeFormat("en-GB", {
        weekday: "short", day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", timeZoneName: "short",
      }).format(new Date(iso));
    } catch { return iso; }
  };

  return (
    <div className="border border-border rounded-xl bg-white" data-testid={`sender-details-${emailId}`}>
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-3.5 py-2 text-xs text-muted-foreground hover:bg-muted/40 rounded-xl transition-colors"
        data-testid={`sender-details-toggle-${emailId}`}
      >
        <span className="font-mono uppercase tracking-widest text-[10px]">Sender details</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-t border-border px-4 py-3 space-y-1.5 text-xs">
              {loading && <div className="text-muted-foreground">Loading…</div>}
              {data && (
                <>
                  <Row k="from" testid={`sd-from-${emailId}`}>
                    <span className="font-medium">{data.from?.name || data.from?.email}</span>
                    {data.from?.name && data.from?.email && <span className="text-muted-foreground ml-1">&lt;{data.from.email}&gt;</span>}
                    <InlineCopy value={data.from?.email} testid={`sd-from-copy-${emailId}`} />
                  </Row>
                  <Row k="to" testid={`sd-to-${emailId}`}>
                    <span className="truncate">{(data.to || []).join(", ") || "—"}</span>
                    <InlineCopy value={(data.to || []).join(", ")} testid={`sd-to-copy-${emailId}`} />
                  </Row>
                  {data.cc && data.cc.length > 0 && (
                    <Row k="cc" testid={`sd-cc-${emailId}`}>
                      <span className="truncate">{data.cc.join(", ")}</span>
                      <InlineCopy value={data.cc.join(", ")} testid={`sd-cc-copy-${emailId}`} />
                    </Row>
                  )}
                  <Row k="date" testid={`sd-date-${emailId}`}>
                    <span>{fmtDate(data.date)}</span>
                  </Row>
                  <Row k="subject" testid={`sd-subject-${emailId}`}>
                    <span className="font-medium truncate">{data.subject || "—"}</span>
                    <InlineCopy value={data.subject} testid={`sd-subject-copy-${emailId}`} />
                  </Row>
                  <Row k="mailed-by" testid={`sd-mailedby-${emailId}`} icon={<Server className="h-3 w-3 text-emerald-brand" />}>
                    <span className="font-mono">{data.mailed_by || "—"}</span>
                    <InlineCopy value={data.mailed_by} testid={`sd-mailedby-copy-${emailId}`} />
                  </Row>
                  <Row k="signed-by" testid={`sd-signedby-${emailId}`} icon={<KeyRound className="h-3 w-3 text-yellow-600" />}>
                    <span className="font-mono">{data.signed_by || "—"}</span>
                    <InlineCopy value={data.signed_by} testid={`sd-signedby-copy-${emailId}`} />
                  </Row>
                  <Row k="security" testid={`sd-security-${emailId}`} icon={<ShieldCheck className="h-3 w-3 text-crimson" />}>
                    <span>{data.security || "—"}</span>
                    <a href="https://support.google.com/mail/answer/6330403" target="_blank" rel="noreferrer" className="ml-1 text-[11px] text-emerald-brand hover:underline inline-flex items-center gap-0.5">
                      Learn more <ArrowRight className="h-3 w-3" />
                    </a>
                  </Row>
                  {(data.spf || data.dkim || data.dmarc) && (
                    <Row k="verdicts" testid={`sd-verdicts-${emailId}`}>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {data.spf && <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${verdictColor(data.spf)}`}>SPF {data.spf}</span>}
                        {data.dkim && <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${verdictColor(data.dkim)}`}>DKIM {data.dkim}</span>}
                        {data.dmarc && <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${verdictColor(data.dmarc)}`}>DMARC {data.dmarc}</span>}
                      </div>
                    </Row>
                  )}
                  {data.message_id && (
                    <Row k="message-id" testid={`sd-msgid-${emailId}`}>
                      <span className="font-mono text-[10px] truncate">{data.message_id}</span>
                      <InlineCopy value={data.message_id} testid={`sd-msgid-copy-${emailId}`} />
                    </Row>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Row({ k, children, testid, icon }) {
  return (
    <div className="grid grid-cols-[100px_1fr] gap-2 items-center" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        {icon}
        <span>{k}</span>
      </div>
      <div className="flex items-center gap-2 min-w-0 text-foreground">
        {children}
      </div>
    </div>
  );
}
